import type { SpecType } from '@local/lib';

import {
  Button,
  Card,
  Combobox,
  Icon,
  Input,
  ScopedNotification,
  Spinner,
  Textarea,
} from '@salesforce/design-system-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';

import type { SpecDefinition } from '../../quality-formulas/qualityFormulaTypes';
import type { QualityAdjustmentTemplate } from '../templateTypes';

import { PageMeta, QualityAdjustmentNavigation } from '../../../shared/components';
import { IconCategory } from '../../../shared/types/Enum';
import {
  showErrorNotification,
  showSuccessNotification,
  showValidationErrorNotification,
} from '../../../shared/utils/notifications';
import { AnimatedItem } from '../../quality-formulas/components/AnimatedItem';
import { FieldLabelRow } from '../../quality-formulas/components/FieldLabelRow';
import { PlaygroundPanel } from '../../quality-formulas/components/PlaygroundPanel';
import { QualityResultsSection } from '../../quality-formulas/components/QualityResultsSection';
import { QualitySpecCard } from '../../quality-formulas/components/QualitySpecCard';
import { createInitialSpecStates } from '../../quality-formulas/qualityFormulaDefaults';
import '../../quality-formulas/components/qualitySpecsWorkbench.css';
import { SPEC_TYPES } from '../../quality-formulas/qualityFormulaMetadata';
import { validateEnabledSpecStates } from '../../quality-formulas/qualityFormulaValidation';
import {
  QualityFormulaStoreProvider,
  useQualityFormulaStore,
  useQualityFormulaStoreApi,
} from '../../quality-formulas/store/QualityFormulaStoreProvider';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { usePermissions } from '../hooks/usePermissions';
import { useTemplate, useTemplateActions } from '../hooks/useTemplates';
import { fromTemplateConfig, toTemplateConfig } from '../templateConfigConversion';
import '../templateEditor.css';

const SPEC_OPTIONS = SPEC_TYPES.map(spec => ({ id: spec.key, label: spec.label }));

// Builds the full SPEC_TYPES-keyed state the quality-formula store expects, seeding it with an
// existing template's saved single-spec config (edit mode) — converted from the API's persisted
// `ITemplateConfig` shape back into this page's form state (see `fromTemplateConfig`) — or leaving
// every spec at its default, disabled state (create mode).
export const buildInitialWorkspaceState = (template?: QualityAdjustmentTemplate) => {
  const specStates = createInitialSpecStates();
  const visibleSpecTypes: SpecType[] = template ? [template.specType] : [];

  if (template) {
    specStates[template.specType] = { ...fromTemplateConfig(template.config), collapsed: false, enabled: true };
  }

  return { specStates, visibleSpecTypes };
};

// Split out so the compact/page heading choice doesn't add to `TemplateEditorForm`'s own
// cyclomatic complexity, which is already near the project's lint threshold.
const TemplateEditorHeading: React.FC<{ compact: boolean; title: string }> = ({ compact, title }) =>
  compact ? (
    <h2 className="slds-text-heading_small">{title}</h2>
  ) : (
    <h1 className="slds-text-heading_medium">{title}</h1>
  );

// Shared chevron-toggle header for the "Manage Templates" (`compact`) view's collapsible
// "Formula Configuration" section — renders a plain, non-collapsible heading when `collapsible` is
// false, so the standalone page/modal keeps its current always-expanded look. The caller wraps this
// (plus the section's body) in the `qfw-collapsible-frame` box — see its own doc comment in
// templateEditor.css for why the box lives on that shared frame rather than on this header alone.
const CollapsibleSectionHeader: React.FC<{
  collapsible: boolean;
  /** SLDS utility icon name shown before the title (e.g. "settings", "path_experiment") — see the
   * utility icon sprite at public/assets/icons/utility-sprite. Only Manage Templates (`compact`)
   * shows one; the standalone page/modal's plain heading is unchanged. */
  iconName?: string;
  onToggle: () => void;
  opened: boolean;
  title: string;
}> = ({ collapsible, iconName, onToggle, opened, title }) => (
  <div
    className={
      collapsible
        ? `qfw-collapsible-frame-header slds-grid slds-grid_align-spread slds-grid_vertical-align-center${
            opened ? ' qfw-collapsible-frame-header_divided' : ''
          }`
        : 'slds-grid slds-grid_align-spread slds-grid_vertical-align-center'
    }
  >
    <span className="slds-grid slds-grid_vertical-align-center" style={{ gap: 'var(--slds-g-spacing-2)' }}>
      {iconName ? (
        <Icon
          category={IconCategory.Utility}
          containerClassName={`slds-icon_container slds-icon-utility-${iconName}`}
          name={iconName}
          size="small"
          // Matches SpecSectionPanel's own section-header icon fill — see its doc comment: every
          // section header icon in the Ash/Sulfur/BTU Cosmos references renders in brand blue, not
          // the neutral gray `slds-current-color` otherwise inherits.
          style={{ fill: 'var(--slds-g-color-accent-1)' }}
        />
      ) : null}
      <h2 className="slds-text-heading_small">{title}</h2>
    </span>
    {collapsible ? (
      <div className="qfw-section-actions">
        <Button
          assistiveText={{ icon: opened ? `Collapse ${title}` : `Expand ${title}` }}
          className="qfw-section-toggle"
          iconCategory={IconCategory.Utility}
          iconName={opened ? 'chevronup' : 'chevrondown'}
          onClick={onToggle}
          variant="icon"
        />
      </div>
    ) : null}
  </div>
);

// Split out, like `TemplateEditorHeading` above, so the compact/page layout choice doesn't add to
// `TemplateEditorForm`'s own cyclomatic complexity. Manage Templates (`compact`) renders the same
// live Playground table the main Quality Adjustment tab uses (`workbench` — per-spec test values
// and impact update inline, no explicit "Test" button and no separate Review Results card needed)
// instead of the standalone page/modal's own Test-button-and-separate-results layout, for visual
// and behavioral parity with the rest of the Quality Adjustment workbench. Manage Templates also
// encloses the whole Playground+Result block in the same `qfw-collapsible-frame` box Formula
// Configuration uses, under a single "Playground" heading/chevron — `PlaygroundScenario`'s own
// Playground/Result cards keep their usual bordered-box look inside it, matching the Quality
// Adjustment tab exactly.
const TemplateTestingLayout: React.FC<{
  compact: boolean;
  onTogglePlayground: () => void;
  playgroundOpen: boolean;
  selectedSpec: SpecDefinition;
}> = ({ compact, onTogglePlayground, playgroundOpen, selectedSpec }) =>
  compact ? (
    <section className="qfw-collapsible-frame slds-box">
      <CollapsibleSectionHeader
        collapsible
        iconName="path_experiment"
        onToggle={onTogglePlayground}
        opened={playgroundOpen}
        title="Playground"
      />
      {playgroundOpen ? (
        <div className="qfw-collapsible-frame-body qfw-container">
          <PlaygroundPanel enabledSpecs={[selectedSpec]} showPlaygroundHeading={false} workbench />
        </div>
      ) : null}
    </section>
  ) : (
    <div className="template-testing-layout">
      <PlaygroundPanel
        description="Enter sample values to see how this template adjusts price and quantity — nothing here is saved."
        enabledSpecs={[selectedSpec]}
        testButtonLabel="Test This Formula"
      />
      <QualityResultsSection compact showEmptyState />
    </div>
  );

interface TemplateEditorFormProps {
  /** Downgrades the "Create/Edit/View Template" heading from the page-level heading scale
   * to the same `slds-text-heading_small` scale `QualitySpecsWorkbench` uses for its own nested section
   * headings — set this when the form renders inside a surface that already has its own Cosmos
   * page/card heading (e.g. `TemplateManagerPanel`'s "Manage Templates" card), so the two headings
   * step down in hierarchy instead of both competing at page scale. */
  compact?: boolean;
  /** Called instead of navigating to `/templates` on Cancel/Save/discard-confirm — used when this
   * form is embedded in a `Modal` (see `TemplateEditorModal`) rather than mounted at its own route. */
  onDone?: () => void;
  template?: QualityAdjustmentTemplate;
  /** `'page'` (default) renders the full routed page, including `PageMeta` and
   * `QualityAdjustmentNavigation`. `'embedded'` renders just the form itself, for use inside a
   * `Modal` that already provides its own chrome. */
  variant?: 'embedded' | 'page';
}

// Split out from `TemplateEditorPage` so it only ever mounts once the template it's editing (if
// any) has finished loading — the quality-formula store's initial state is seeded once, by the
// enclosing `QualityFormulaStoreProvider`, so this component must not render before that data is
// ready.
export const TemplateEditorForm: React.FC<TemplateEditorFormProps> = ({
  compact = false,
  onDone,
  template,
  variant = 'page',
}) => {
  const navigate = useNavigate();
  const handleDone = () => {
    if (onDone) {
      onDone();
    } else {
      void navigate('/templates');
    }
  };
  const { canManageTemplates } = usePermissions();
  const { createTemplate, updateTemplate } = useTemplateActions();
  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [isSaving, setIsSaving] = useState(false);
  // Tracks whether the form or the quality-formula store has changed since mount, so Cancel and a
  // browser tab close can both warn before discarding progress. Name/description are flagged
  // directly by their own onChange (local component state); any store mutation (selecting a spec,
  // editing its formula rows, etc.) is caught by subscribing to the store itself below.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  // Only Manage Templates (`compact`) shows the "Formula Configuration"/"Playground" collapse/expand
  // chevrons; the standalone page/modal keeps both sections always expanded.
  const [isFormulaConfigOpen, setIsFormulaConfigOpen] = useState(true);
  const [isPlaygroundOpen, setIsPlaygroundOpen] = useState(true);

  const { expandSpecs, handleVisibleSpecTypesChange, visibleSpecTypes } = useQualityFormulaStore(
    useShallow(store => ({
      expandSpecs: store.expandSpecs,
      handleVisibleSpecTypesChange: store.handleVisibleSpecTypesChange,
      visibleSpecTypes: store.visibleSpecTypes,
    })),
  );
  // Read via the store API (not `useQualityFormulaStore`) so this component isn't subscribed to —
  // and doesn't re-render on — every `specStates` change; `handleSave` only needs a snapshot at
  // submit time, not live state to render from.
  const storeApi = useQualityFormulaStoreApi();

  // Marks the form dirty only on a mutation to what `handleSave` actually persists — this spec's
  // own formula config (`specStates`) and which spec is selected (`visibleSpecTypes`) — not the
  // Formula Testing panel's sample values or preview results (`testValuesSlice`/`runPreviewSlice`),
  // which are explicitly never saved (see FormulaTestingPanel's own description text). The plain
  // vanilla store's `subscribe` always hands the listener `(state, prevState)`, so comparing just
  // these two references is enough without pulling in a selector-subscription middleware. The
  // initial seeding happens before this subscription exists, so it only ever fires on genuine
  // user changes.
  useEffect(
    () =>
      storeApi.subscribe((state, prevState) => {
        if (state.specStates !== prevState.specStates || state.visibleSpecTypes !== prevState.visibleSpecTypes) {
          setHasUnsavedChanges(true);
        }
      }),
    [storeApi],
  );

  // Warns on an actual tab close/reload, not just navigating away within the app (Cancel/Save
  // handle that themselves) — only attached while there's something to lose.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // A template belongs to exactly one Quality Specification, so at most one entry of
  // `visibleSpecTypes` is ever meaningful here — `handleVisibleSpecTypesChange` already knows how
  // to enable one spec and disable whatever was selected before. `.at(0)` (rather than `[0]`) keeps
  // this properly typed as possibly-undefined even without the project's tsconfig enabling
  // `noUncheckedIndexedAccess`.
  const selectedSpecType = visibleSpecTypes.at(0) ?? null;
  const selectedSpec = selectedSpecType ? SPEC_TYPES.find(spec => spec.key === selectedSpecType) : undefined;
  const selectedSpecOption = selectedSpecType ? SPEC_OPTIONS.find(option => option.id === selectedSpecType) : undefined;

  let pageTitle = 'View Template';
  if (canManageTemplates) {
    pageTitle = template ? 'Edit Template' : 'Create Template';
  }

  const handleSpecChange = (value: SpecType) => {
    handleVisibleSpecTypesChange([value]);
    expandSpecs([value]);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setIsCancelConfirmOpen(true);
      return;
    }
    handleDone();
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      showErrorNotification(new Error('Template name is required'), 'Unable to save template');
      return;
    }
    if (!selectedSpecType) {
      showValidationErrorNotification('Cannot save template.', ['Select a Quality Specification.']);
      return;
    }

    // Same business-rule checks "Test This Formula" runs (zero rates, overlapping ranges, etc.) —
    // the shared Zod schema is still the actual source of truth (see `toTemplateConfig`'s POST/PUT),
    // but failing fast here gives a precise, field-level message instead of a generic API rejection.
    const { specStates } = storeApi.getState();
    const missingFields = validateEnabledSpecStates(specStates);
    if (missingFields.length > 0) {
      showValidationErrorNotification(
        'Cannot save template.',
        missingFields.map(field => `${field.fieldLabel}: ${field.reason}`),
      );
      return;
    }

    // Only the fields that define a formula are persisted — `collapsed`/`enabled` are this page's
    // own UI state, not part of what the template means (mirrors the shape TemplateSpecConfig
    // documents in templateTypes.ts). Converted to the API's persisted `ITemplateConfig` shape
    // (see `toTemplateConfig`) so it's validated against the same formula schemas the
    // quality-adjustments calculate endpoint uses.
    const { collapsed: _collapsed, enabled: _enabled, ...specConfig } = specStates[selectedSpecType];
    const config = toTemplateConfig(specConfig, selectedSpecType);
    const trimmedDescription = description.trim();

    setIsSaving(true);
    try {
      const input = {
        config,
        description: trimmedDescription === '' ? undefined : trimmedDescription,
        name: trimmedName,
        specType: selectedSpecType,
      };
      if (template) {
        await updateTemplate(template.id, input);
        showSuccessNotification(`Saved changes to "${trimmedName}".`);
      } else {
        await createTemplate(input);
        showSuccessNotification(`Created template "${trimmedName}".`);
      }
      handleDone();
    } catch (error) {
      showErrorNotification(error, 'Unable to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const formBody = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-4)' }}>
      <div
        className="slds-grid slds-grid_align-spread slds-grid_vertical-align-start slds-wrap"
        style={{ gap: 'var(--slds-g-spacing-4)' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-1)', maxWidth: 640 }}>
          <TemplateEditorHeading compact={compact} title={pageTitle} />
          <span className="slds-text-body_regular slds-text-color_weak">
            Templates provide default Quality Adjustment values contracts can load — saving changes here never affects a
            contract that already loaded this template.
          </span>
        </div>
        <div className="slds-grid" style={{ gap: 'var(--slds-g-spacing-2)' }}>
          <Button
            iconCategory={IconCategory.Utility}
            iconName={canManageTemplates ? 'close' : 'back'}
            iconPosition="left"
            label={canManageTemplates ? 'Cancel' : 'Back'}
            onClick={handleCancel}
            variant="neutral"
          />
          {canManageTemplates ? (
            <Button
              disabled={isSaving}
              iconCategory={IconCategory.Utility}
              iconName={isSaving ? undefined : 'save'}
              iconPosition="left"
              onClick={() => {
                void handleSave();
              }}
              variant="brand"
            >
              {isSaving ? (
                <Spinner
                  containerStyle={{ marginInlineEnd: 'var(--slds-g-spacing-2)' }}
                  hasContainer={false}
                  isInline
                  size="x-small"
                />
              ) : null}
              Save Template
            </Button>
          ) : null}
        </div>
      </div>

      {canManageTemplates ? null : (
        <ScopedNotification theme="light">
          <p className="slds-text-body_small slds-text-color_weak">
            You have read-only access to templates. Only Admin users can modify this configuration.
          </p>
        </ScopedNotification>
      )}

      <fieldset disabled={!canManageTemplates} style={{ border: 'none', margin: 0, padding: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-4)' }}>
          <Card bodyClassName="slds-card__body_inner" hasNoHeader>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-3)' }}>
              <div className="slds-grid slds-wrap slds-gutters_small">
                <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
                  <Input
                    assistiveText={{ label: 'Template Name' }}
                    label="Template Name"
                    onChange={(_event, data) => {
                      setHasUnsavedChanges(true);
                      setName(data.value);
                    }}
                    placeholder="e.g. Standard Moisture Discount"
                    required
                    value={name}
                  />
                </div>
                <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-2">
                  <FieldLabelRow label="Quality Specification" />
                  <Combobox
                    assistiveText={{ label: 'Quality Specification' }}
                    events={{
                      onSelect: (_event, data) => {
                        const selectedId = data.selection[0]?.id;
                        if (!selectedId) return;
                        // Pre-existing cross-package enum-identity lint quirk — see api/pricingEngineApi.ts.
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
                        handleSpecChange(selectedId as SpecType);
                      },
                    }}
                    id="template-spec-select"
                    labels={{ placeholder: 'Select a quality specification' }}
                    options={SPEC_OPTIONS}
                    predefinedOptionsOnly
                    selection={selectedSpecOption ? [selectedSpecOption] : []}
                    variant="readonly"
                  />
                </div>
              </div>
              <Textarea
                label="Description"
                onChange={event => {
                  setHasUnsavedChanges(true);
                  setDescription(event.target.value);
                }}
                placeholder="Optional business-friendly note about when to use this template"
                value={description}
              />
            </div>
          </Card>

          {selectedSpec ? (
            <AnimatedItem visible>
              {styles => (
                <section
                  aria-label="Configure the template's formula"
                  className={compact ? 'qfw-collapsible-frame slds-box' : undefined}
                  style={{
                    ...styles,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: compact ? 0 : 'var(--slds-g-spacing-3)',
                  }}
                >
                  <CollapsibleSectionHeader
                    collapsible={compact}
                    iconName={compact ? 'settings' : undefined}
                    onToggle={() => {
                      setIsFormulaConfigOpen(previous => !previous);
                    }}
                    opened={isFormulaConfigOpen}
                    title="Formula Configuration"
                  />
                  {compact && !isFormulaConfigOpen ? null : (
                    <div
                      className={compact ? 'qfw-collapsible-frame-body' : undefined}
                      id={`spec-card-${selectedSpec.key}`}
                    >
                      <QualitySpecCard
                        collapsible={false}
                        hideContractPriceInfo={!template}
                        hint={selectedSpec.hint}
                        priceBasisLocked
                        specLabel={selectedSpec.label}
                        specType={selectedSpec.key}
                      />
                    </div>
                  )}
                </section>
              )}
            </AnimatedItem>
          ) : null}
        </div>
      </fieldset>

      {selectedSpec ? (
        <TemplateTestingLayout
          compact={compact}
          onTogglePlayground={() => {
            setIsPlaygroundOpen(previous => !previous);
          }}
          playgroundOpen={isPlaygroundOpen}
          selectedSpec={selectedSpec}
        />
      ) : null}
    </div>
  );

  return (
    <>
      {variant === 'page' ? (
        <div
          className="slds-p-bottom_x-large slds-p-horizontal_medium slds-p-top_small"
          style={{ margin: '0 auto', maxWidth: 1180, width: '100%' }}
        >
          <PageMeta title={template ? `Edit ${template.name}` : 'New Template'} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-4)' }}>
            <QualityAdjustmentNavigation />
            {formBody}
          </div>
        </div>
      ) : (
        formBody
      )}

      <ConfirmationModal
        confirmText="Discard"
        onCancel={() => {
          setIsCancelConfirmOpen(false);
        }}
        onConfirm={() => {
          setIsCancelConfirmOpen(false);
          handleDone();
        }}
        opened={isCancelConfirmOpen}
        title="Discard unsaved changes?"
      >
        <p className="slds-text-body_small">
          You have unsaved changes to this template. Leaving now will discard them — this can’t be undone.
        </p>
      </ConfirmationModal>
    </>
  );
};

/**
 * Route shell for both `/templates/new` and `/templates/:id/edit` — waits for the existing
 * template (edit mode) to finish loading, then mounts a `QualityFormulaStoreProvider` (seeded
 * from that template's saved config, if any) keyed by the template id, so switching between
 * templates always starts from a fresh store instance instead of reusing state from whichever
 * template was previously open.
 */
export const TemplateEditorPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { data: template, isFetching, isLoading } = useTemplate(id ?? null);

  // `isLoading` alone only covers the very first fetch — `useTemplate`'s mandatory
  // `refetchOnMount: 'always'` refetch on every visit still reports cached (possibly stale) data
  // with `isLoading: false` while that refetch is in flight. `TemplateEditorForm` only ever reads
  // `template` once, at mount, so it must not mount until that refetch has actually settled.
  if (id && (isLoading || isFetching)) {
    return (
      <div
        className="slds-p-bottom_x-large slds-p-horizontal_medium slds-p-top_small"
        style={{ margin: '0 auto', maxWidth: 1180, width: '100%' }}
      >
        <QualityAdjustmentNavigation />
        <div className="slds-grid slds-grid_align-center slds-p-around_x-large">
          <Spinner hasContainer={false} />
        </div>
      </div>
    );
  }

  const resolvedTemplate = id ? template : undefined;
  const initialState = buildInitialWorkspaceState(resolvedTemplate);

  return (
    <QualityFormulaStoreProvider
      initialSpecStates={initialState.specStates}
      initialVisibleSpecTypes={initialState.visibleSpecTypes}
      key={id ?? 'new'}
    >
      <TemplateEditorForm template={resolvedTemplate} />
    </QualityFormulaStoreProvider>
  );
};
